import axios from "axios";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useNavigate, useLocation } from "react-router-dom";
import L from "leaflet";
import { MapContainer, Polygon, TileLayer, useMap, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

// Fix default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const containerStyle = {
  width: "100%",
  height: "50vh",
};
const center = {
  lat: 28.641832439372656,
  lng: 77.22480777651072,
};
const bhuvanLayerUrl = 'https://bhuvan-ras1.nrsc.gov.in/tilecache/tilecache.py';

const WMSTileLayer = ({ url, layers, ...props }) => {
  const map = useMap();
  useEffect(() => {
    const wmsLayer = L.tileLayer.wms(url, {
      layers: layers,
      format: 'image/png',
      transparent: true,
      attribution: 'Bhuvan NRSC',
      maxZoom: 18,
      version: '1.1.1',
      srs: 'EPSG:3857',
      Maxresolution: 156543.033928,
      styles: '',
      ...props
    }).addTo(map);
    return () => map.removeLayer(wmsLayer);
  }, [map, url, layers, props]);
  return null;
};

// AutoZoom component
const AutoZoomToPolygon = ({ polygons }) => {
  const map = useMap();
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (zoomed) return;
    
    if (!polygons || polygons.length === 0) {
      return;
    }

    try {
      const bounds = L.latLngBounds();
      let hasValidCoords = false;

      polygons.forEach((polygon) => {
        if (polygon && polygon.length > 0) {
          polygon.forEach(coord => {
            if (Array.isArray(coord) && coord.length === 2) {
              bounds.extend([coord[0], coord[1]]);
              hasValidCoords = true;
            }
          });
        }
      });

      if (hasValidCoords && bounds.isValid()) {
        setTimeout(() => {
          map.fitBounds(bounds, { 
            padding: [50, 50],
            maxZoom: 20
          });
          setZoomed(true);
        }, 100);
      }
    } catch (error) {
      console.error("Error auto-zooming to polygon:", error);
    }
  }, [polygons, map, zoomed]);

  return null;
};

// Component to add markers at polygon centers
const PolygonMarkers = ({ polygons }) => {
  const map = useMap();
  
  if (!polygons || polygons.length === 0) return null;
  
  return (
    <>
      {polygons.map((polygon, index) => {
        try {
          if (!polygon || polygon.length < 3) return null;
          
          // Calculate center of polygon (average of all coordinates)
          let lat = 0, lng = 0;
          let validCoords = 0;
          
          polygon.forEach(coord => {
            if (Array.isArray(coord) && coord.length === 2) {
              const latVal = coord[0];
              const lngVal = coord[1];
              if (latVal >= -90 && latVal <= 90 && lngVal >= -180 && lngVal <= 180) {
                lat += latVal;
                lng += lngVal;
                validCoords++;
              }
            }
          });
          
          if (validCoords === 0) return null;
          
          const centerLat = lat / validCoords;
          const centerLng = lng / validCoords;
          
          return (
            <Marker 
              key={`marker-${index}`} 
              position={[centerLat, centerLng]}
              eventHandlers={{
                click: () => {
                  map.setView([centerLat, centerLng], 20);
                }
              }}
            >
              <Popup>
                <div>
                  <strong>Polygon {index + 1}</strong>
                  <br />
                  Click to zoom in
                </div>
              </Popup>
            </Marker>
          );
        } catch (error) {
          console.error(`Error creating marker for polygon ${index}:`, error);
          return null;
        }
      })}
    </>
  );
};

// Accepts optional `farmerCode` prop when rendered inside FarmerDetailView
const NMEOOSDeatilPage = ({ farmerCode: farmerCodeProp } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cropSurveyData, setCropSurveyData] = useState(null);
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [parsedPolygons, setParsedPolygons] = useState([]);

  const sourceRow = farmerCodeProp || location.state?.farmerCode;
  const farmerId = sourceRow?.farmerId ?? sourceRow?.farmerID ?? sourceRow?.id ?? null;
  const schemeCodeModel = sessionStorage.getItem("schemeCodeModel");

  // Fetch detail data when farmerId is available
  useEffect(() => {
    if (farmerId && schemeCodeModel === "5" && !fetched) {
      fetchDetailedDataNMEOOP();
    }
  }, [farmerId, schemeCodeModel]);

  // Parse polygons when data changes - FIXED for object format
  useEffect(() => {
    if (cropSurveyData?.farmerCropSurveyPolygonList) {
      const polygons = cropSurveyData.farmerCropSurveyPolygonList
        .map((item) => {
          try {
            const coords = JSON.parse(item.polygonShape);
            
            // Check if coords is an array of objects with lat/lng
            if (Array.isArray(coords) && coords.length > 0 && coords[0].lat !== undefined) {
              // Convert from [{lat, lng}] to [[lat, lng]]
              return coords.map(coord => [coord.lat, coord.lng]);
            }
            
            // If it's already in [[lat, lng]] format
            if (Array.isArray(coords) && coords.length > 0 && Array.isArray(coords[0])) {
              // Check if we need to swap (if first value > 90, it's longitude)
              if (Math.abs(coords[0][0]) > 90) {
                return coords.map(coord => [coord[1], coord[0]]);
              }
              return coords;
            }
            
            return coords;
          } catch (error) {
            console.error("Error parsing polygon:", error);
            return null;
          }
        })
        .filter(polygon => polygon !== null);
      
      setParsedPolygons(polygons);
    } else {
      setParsedPolygons([]);
    }
  }, [cropSurveyData]);

  async function fetchDetailedDataNMEOOP() {
    if (fetched) return;

    try {
      setLoading(true);

      const apiEndpoint = `${
        process.env.NODE_ENV === "development"
          ? process.env.REACT_APP_DEV_MODE
          : process.env.REACT_APP_PRO_MODE
      }/api/NMEOS/getNMEOSFarmerDetailsAll`;

      const requestBody = {
        SchemeCode: "5",
        lngA_FarmerCode: farmerId,
      };

      const result = await axios.post(apiEndpoint, requestBody);

      if (result.status !== 200) {
        console.error("Error fetching data:", result.statusText);
        setCropSurveyData(null);
        return;
      }

      if (!result.data || !result.data.dataContent) {
        console.error("Invalid response structure. Expected dataContent array.");
        setCropSurveyData(null);
        return;
      }

      if (result.data.dataContent.length === 0) {
        console.log("No data found for this farmer");
        setCropSurveyData(null);
        return;
      }

      const farmerRecord = result.data.dataContent.find(
        (farmer) =>
          farmer.farmerId === farmerId ||
          farmer.farmerID === farmerId ||
          farmer.ingA_FarmerCode === farmerId ||
          farmer.farmerCode === farmerId
      ) || result.data.dataContent[0];

      if (!farmerRecord) {
        console.log("No data found for this specific farmer");
        setCropSurveyData(null);
        return;
      }

      setCropSurveyData(farmerRecord);
      setFetched(true);
    } catch (error) {
      console.error("Error fetching NMEOOP details:", error);
      setCropSurveyData(null);
    } finally {
      setLoading(false);
    }
  }

  const handleActiveClick = (imagePath) => {
    setActiveImage(imagePath);
  };

  if (!cropSurveyData && !loading) {
    return (
      <div className="container mt-4">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-info">
              <h4>No Data Available</h4>
              <p>Farmer ID: {farmerId ?? 'Not provided'}</p>
              <p>Scheme Code: {schemeCodeModel}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main id="main" className="main">
      <div className="pagetitle page_title_box d-flex align-items-center justify-content-between">
        <nav>
          <h1>Survey Details</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Farmer</li>
            <li className="breadcrumb-item active">Farmer Details View</li>
          </ol>
        </nav>
        <button
          type="button"
          className="btn btn-success"
          onClick={() => navigate(-1)}
        >
          <i>
            <FontAwesomeIcon icon={faArrowLeft} />
          </i>{" "}
          Back
        </button>
      </div>

      <section className="section">
        {loading && (
          <div className="row">
            <div className="col-12 text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading farmer details...</p>
            </div> 
          </div>
        )}

        {!loading && !cropSurveyData && (
          <div className="row">
            <div className="col-12 text-center">
              <div className="alert alert-info">
                <h4>No Data Available</h4>
                <p>Farmer ID: {farmerId ?? 'Not provided'}</p>
                <p>Scheme Code: {schemeCodeModel || 'Not set'}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && cropSurveyData && (
          <div className="row">
            <div className="col-lg-12 dashboard">
              <div className="card">
                <div className="card-body p-5 pt-2">
                  {/* Polygon Details */}
                  <div className="row cropview">
                    <h5 className="formgrouping">Polygon Details</h5>
                    <MapContainer
                      center={[center.lat, center.lng]}
                      zoom={18}
                      style={containerStyle}
                      scrollWheelZoom
                    >
                      <WMSTileLayer url={bhuvanLayerUrl} layers="bhuvan_wbase_ind" />
                      
                      {/* Render polygons from parsed data */}
                      {parsedPolygons.map((coordinates, index) => (
                        <Polygon
                          key={`polygon-${index}`}
                          positions={coordinates}
                          pathOptions={{
                            color: "#FF0000",
                            fillColor: "#FF0000",
                            fillOpacity: 0.1,
                            weight: 2,
                          }}
                        />
                      ))}
                      
                      {/* Render markers at polygon centers */}
                      <PolygonMarkers polygons={parsedPolygons} />
                      
                      {/* Auto-zoom to polygon location */}
                      <AutoZoomToPolygon polygons={parsedPolygons} />
                    </MapContainer>
                  </div>

                  {/* Survey Details */}
                  <div className="row cropview">
                    <h5 className="formgrouping">Survey Details</h5>
                    <div className="row surveydetailsbg">
                      {cropSurveyData.schemeName && (
                        <div className="col-md-3">
                          <h6>Scheme Name</h6>
                          <p>{cropSurveyData.schemeName}</p>
                        </div>
                      )}
                      {cropSurveyData.surveyUserCode && (
                        <div className="col-md-3">
                          <h6>Survey No.</h6>
                          <p>{cropSurveyData.surveyUserCode}</p>
                        </div>
                      )}
                      {cropSurveyData.agencyName && (
                        <div className="col-md-3">
                          <h6>Agency Name</h6>
                          <p>{cropSurveyData.agencyName}</p>
                        </div>
                      )}
                      {cropSurveyData.userName && (
                        <div className="col-md-3">
                          <h6>User Name</h6>
                          <p>{cropSurveyData.userName}</p>
                        </div>
                      )}
                      {cropSurveyData.farmerName && (
                        <div className="col-md-3">
                          <h6>Farmer Name</h6>
                          <p>{cropSurveyData.farmerName}</p>
                        </div>
                      )}
                      {cropSurveyData.farmerID && (
                        <div className="col-md-3">
                          <h6>Farmer Id</h6>
                          <p>{cropSurveyData.farmerID}</p>
                        </div>
                      )}
                      {cropSurveyData.stateName && (
                        <div className="col-md-3">
                          <h6>State</h6>
                          <p>{cropSurveyData.stateName}</p>
                        </div>
                      )}
                      {cropSurveyData.districtName && (
                        <div className="col-md-3">
                          <h6>District</h6>
                          <p>{cropSurveyData.districtName}</p>
                        </div>
                      )}
                      {cropSurveyData.subDistrictName && (
                        <div className="col-md-3">
                          <h6>Sub District</h6>
                          <p>{cropSurveyData.subDistrictName}</p>
                        </div>
                      )}
                      {cropSurveyData.villageName && (
                        <div className="col-md-3">
                          <h6>Village</h6>
                          <p>{cropSurveyData.villageName}</p>
                        </div>
                      )}
                      {cropSurveyData.mobileNo && (
                        <div className="col-md-3">
                          <h6>Mobile No</h6>
                          <p>{cropSurveyData.mobileNo}</p>
                        </div>
                      )}
                      <div className="col-md-3">
                        <h6>Created Date</h6>
                        <p>
                          {(() => {
                            const dateValue = cropSurveyData.createdOn;
                            if (dateValue) {
                              try {
                                const date = new Date(dateValue);
                                if (!isNaN(date.getTime())) {
                                  return date.toLocaleDateString();
                                }
                              } catch (error) {
                                console.error('Error parsing date:', error);
                              }
                            }
                            return '';
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Farmer Plot Registration List */}
                  {cropSurveyData.farmerPlotRegistrationList && 
                    cropSurveyData.farmerPlotRegistrationList.length > 0 && (
                    <div className="row cropview">
                      <h5 className="formgrouping">Farmer Plot Registration List</h5>
                      <div className="table-responsive newtable">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>farmerCode</th>
                              <th>Crop Stage Name</th>
                              <th>Current Date</th>
                              <th>Last Visit Date</th>
                              <th>Created On By UC</th>
                              <th>UserName</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cropSurveyData.farmerPlotRegistrationList.map((item, index) => (
                              <tr key={item.farmerCode || index}>
                                <td>{index + 1}</td>
                                <td>{item.farmerCode || "N/A"}</td>
                                <td>{item.cropStageName || "N/A"}</td>
                                <td>{item.currentDate ? new Date(item.currentDate).toLocaleDateString("en-CA") : "N/A"}</td>
                                <td>{item.lastVisitDate ? new Date(item.lastVisitDate).toLocaleDateString("en-CA") : "N/A"}</td>
                                <td>{item.createdOnByUC ? new Date(item.createdOnByUC).toLocaleDateString("en-CA") : "N/A"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Farmer Aadhaar Image Section */}
                  {cropSurveyData?.farmerAadhaarImageList?.length > 0 && (
                    <div className="row cropview">
                      <h5 className="formgrouping">Farmer Aadhaar Image</h5>
                      <div className="row">
                        {cropSurveyData.farmerAadhaarImageList.map((item, index) => (
                          <div key={index} className="col-md-2">
                            <img
                              src={item.attPath}
                              alt="Aadhaar Image"
                              className="img-fluid imgdetials"
                              data-bs-toggle="modal"
                              data-bs-target="#imageModal"
                              onClick={() => handleActiveClick(item.attPath)}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Farmer Seed Distribution Image Section */}
                  {cropSurveyData?.farmerSeedDistributionImageList?.length > 0 && (
                    <div className="row cropview">
                      <h5 className="formgrouping">Farmer Seed Distribution Image</h5>
                      {cropSurveyData.farmerSeedDistributionImageList.map((item, idx) => (
                        <div key={idx} className="col-12 mb-4 p-3 border rounded bg-light">
                          <div className="mb-2">
                            <strong>Seed Quantity (KG):</strong> {item.seedQuantityKG} <br />
                            <strong>Seed Type:</strong> {item.ctlsName} <br />
                            <strong>Distribution Date:</strong> {item.seedDistributionDate}
                          </div>
                          <div className="row">
                            {item.seedDistributionImageLists?.map((img, imgIdx) => (
                              <div className="col-md-2 mb-3" key={imgIdx}>
                                <img
                                  src={img.attPath}
                                  alt="Seed Distribution"
                                  className="img-fluid imgdetials"
                                  data-bs-toggle="modal"
                                  data-bs-target="#imageModal"
                                  onClick={() => handleActiveClick(img.attPath)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Farmer Crop Survey Image */}
                  {cropSurveyData.farmerCropSurveyImageList &&
                    cropSurveyData.farmerCropSurveyImageList.length > 0 && (
                    <div className="row cropview">
                      <h5 className="formgrouping">Farmer Crop Survey Image</h5>
                      {Object.entries(
                        cropSurveyData.farmerCropSurveyImageList.reduce((acc, item) => {
                          if (!acc[item.plotId]) {
                            acc[item.plotId] = [];
                          }
                          acc[item.plotId].push(item);
                          return acc;
                        }, {})
                      ).map(([plotId, images]) => (
                        <div key={plotId} className="row mb-3">
                          {images.map((item, index) => (
                            <React.Fragment key={index}>
                              <div className="col-md-2">
                                <img
                                  src={item.attPath}
                                  alt="Farmer Image"
                                  data-bs-toggle="modal"
                                  data-bs-target="#imageModal"
                                  className="img-fluid imgdetials"
                                  onClick={() => handleActiveClick(item.attPath)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Modal */}
        <div
          className="modal fade"
          id="imageModal"
          tabIndex="-1"
          aria-labelledby="imageModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="imageModalLabel">Image Preview</h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body text-center">
                {activeImage && (
                  <img
                    src={activeImage}
                    alt="Preview"
                    className="img-fluid"
                    style={{ maxHeight: '80vh' }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default NMEOOSDeatilPage;